import { useAppCatalogLocale } from '@/i18n/context';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import storefrontCopy, { type StorefrontCopyKey } from '@/i18n/ns/storefront';
import { WorkspaceIcon } from '../icons';
import type { ProfileSource, StoreProfile } from './api';
import { when } from './format';

const PARTS: Record<Extract<ProfileSource, { kind: 'read' }>['part'], StorefrontCopyKey> = {
  navigation: 'profilePartNavigation',
  welcome: 'profilePartWelcome',
  markets: 'profilePartMarkets',
  functions: 'profilePartFunctions',
  products: 'profilePartProducts',
  pages: 'profilePartPages',
};

function pageLabel(url: string, title?: string): string {
  if (title) return title;
  try {
    const parsed = new URL(url);
    return `${parsed.host}${parsed.pathname === '/' ? '' : parsed.pathname}`;
  } catch {
    return url;
  }
}

/** Where the profile came from: the pages it was written from (with links), then the parts of the read. */
function ProfileSources({ profile }: { profile: StoreProfile }) {
  const copy = useNamespaceCopy(storefrontCopy);
  const sources = profile.sources ?? [];
  const pages = sources.filter((source) => source.kind === 'page');
  const parts = sources.filter((source) => source.kind === 'read');
  if (!pages.length && !parts.length) return null;
  return (
    <div className="sf-profile-sources" data-testid="sf-profile-sources">
      <h4>{copy.profileSources}</h4>
      <ul>
        {pages.map((source) => (
          <li key={source.url}>
            <a href={source.url} target="_blank" rel="noopener noreferrer">
              {pageLabel(source.url, source.title)}
            </a>
          </li>
        ))}
      </ul>
      {parts.length > 0 && (
        <p className="sf-profile-meta">
          {parts.map((source) => copy[PARTS[source.part]]).join(' · ')}
          {profile.products?.length
            ? ` · ${formatCopy(profile.products.length === 1 ? copy.profileSampleOne : copy.profileSample, { count: String(profile.products.length) })}`
            : ''}
        </p>
      )}
    </div>
  );
}

/**
 * The shop's "About this store" profile under What it knows: what a knowledge pass wrote from the
 * shop's own pages (with its sources and the day it was checked), what the assistant can do here,
 * and the lines the team added themselves, which a pass never replaces. Changes happen in Chat.
 */
export function StoreProfileCard({
  profile,
  onChat,
}: {
  profile?: StoreProfile;
  onChat?: () => void;
}) {
  const copy = useNamespaceCopy(storefrontCopy);
  const locale = useAppCatalogLocale() ?? 'en';
  const lines = profile?.additions ?? [];
  return (
    <div className="sf-profile" data-testid="sf-profile">
      <div className="sf-profile-head">
        <h3>{copy.profileTitle}</h3>
        {onChat && (
          <button
            type="button"
            className="ws-button"
            data-testid="sf-profile-chat"
            onClick={onChat}
          >
            <WorkspaceIcon name="chat" size={14} />
            {copy.profileWithChat}
          </button>
        )}
      </div>
      {profile?.text ? (
        <>
          <p className="sf-profile-text" data-testid="sf-profile-text">
            {profile.text}
          </p>
          {profile.assistant && profile.assistant.length > 0 && (
            <div data-testid="sf-profile-assistant">
              <h4>{copy.profileAssistant}</h4>
              <ul className="sf-profile-lines">
                {profile.assistant.map((item) => (
                  <li key={item.ability}>{item.line}</li>
                ))}
              </ul>
            </div>
          )}
          {profile.checkedAt && (
            <p className="sf-profile-meta" data-testid="sf-profile-checked">
              {formatCopy(copy.profileChecked, { date: when(profile.checkedAt, locale) })}
            </p>
          )}
          <ProfileSources profile={profile} />
        </>
      ) : (
        <p className="sf-knows-empty">{copy.profileEmpty}</p>
      )}
      {lines.length > 0 && (
        <div data-testid="sf-profile-team">
          <h4>{copy.profileTeam}</h4>
          <ul className="sf-profile-lines">
            {lines.map((line) => (
              <li key={line.id}>
                {line.text}
                <span className="sf-profile-meta"> · {when(line.at, locale)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
